import { FC, useMemo } from 'react';
import { PlannedSampleOption, useAssignAnimalsContext } from '@/components/Samples/AssignAnimals/AssignAnimalsProvider';
import { Column, FuzzySearchSelect, SelectionValue } from '@/components/UI/FuzzySearchSelect/FuzzySearchSelect';
import { _isNil, _notNil } from '@/littledash';
import { TargetType } from '@/components/Samples/AssignAnimals/AssignAnimal.util';
import { isDisjointFromSets, isSubsetOfSets } from '@/utils/browser.utils';
import {
  GroupTarget,
  PlannedAnimalTarget,
  RegisteredAnimalTarget,
  SampleDefinitionFormData,
  TargetApiId,
} from '@/model/SamplePlan.model';

const columns: Array<Column<PlannedSampleOption>> = [
  {
    id: 'name',
    title: 'Name',
    cell: (data) => (
      <div className="flex flex-column">
        <span className="near-black">{data.name}</span>
        <span className="dark-gray f8">{data.sample_name}</span>
      </div>
    ),
  },
];

const searchIndex = [{ name: 'name' }, { name: 'sample_name' }];

const getSelectedTargetsByType = (
  activeTargetType: TargetType,
  selectedGroupTargets: Set<GroupTarget['api_id']>,
  selectedRegisteredAnimalTargets: Set<RegisteredAnimalTarget['api_id']>,
  selectedPlannedAnimalTargets: Set<PlannedAnimalTarget['api_id']>
): Set<TargetApiId> => {
  switch (activeTargetType) {
    case 'groups':
      return selectedGroupTargets;
    case 'registered_animals':
      return selectedRegisteredAnimalTargets;
    case 'planned_animals':
      return selectedPlannedAnimalTargets;
    default:
      return new Set();
  }
};

const calculateSelectionValue = (
  selectedTargets: Set<TargetApiId>,
  activeTargets: Set<TargetApiId> | undefined
): SelectionValue => {
  if (_isNil(activeTargets)) {
    return false;
  }

  if (isDisjointFromSets(selectedTargets, activeTargets)) {
    return false;
  }

  if (isSubsetOfSets(selectedTargets, activeTargets)) {
    return true;
  }

  return 'indeterminate';
};

export const PlannedSampleSearchSelect: FC = () => {
  const {
    plannedSampleOptions,
    activeTargetType,
    selectedGroupTargets,
    selectedPlannedAnimalTargets,
    selectedRegisteredAnimalTargets,
    plannedSampleToTargetType,
    plannedSampleToTargets,
    updateSelection,
  } = useAssignAnimalsContext();

  const { selected, disabled, disabledItems } = useMemo(() => {
    const selectedTargets = getSelectedTargetsByType(
      activeTargetType,
      selectedGroupTargets,
      selectedRegisteredAnimalTargets,
      selectedPlannedAnimalTargets
    );

    if (selectedTargets.size === 0) {
      return {
        selected: new Map<SampleDefinitionFormData['api_id'], SelectionValue>(
          Array.from(plannedSampleOptions.keys()).map((apiId) => [apiId, false])
        ),
        disabled: true,
        disabledItems: new Set<SampleDefinitionFormData['api_id']>(),
      };
    }

    const result = Array.from(plannedSampleOptions.keys()).reduce<{
      selected: Map<SampleDefinitionFormData['api_id'], SelectionValue>;
      disabledItems: Set<SampleDefinitionFormData['api_id']>;
    }>(
      (acc, plannedSampleApiId) => {
        const targetType = plannedSampleToTargetType.get(plannedSampleApiId);
        const activeTargets = plannedSampleToTargets.get(plannedSampleApiId);

        if (_notNil(targetType) && targetType !== activeTargetType) {
          acc.disabledItems.add(plannedSampleApiId);
          acc.selected.set(plannedSampleApiId, false);
        } else {
          acc.selected.set(
            plannedSampleApiId,
            calculateSelectionValue(selectedTargets as Set<TargetApiId>, activeTargets)
          );
        }

        return acc;
      },
      {
        selected: new Map<SampleDefinitionFormData['api_id'], SelectionValue>(),
        disabledItems: new Set<SampleDefinitionFormData['api_id']>(),
      }
    );

    const allItemsDisabled = plannedSampleOptions.size > 0 && result.disabledItems.size === plannedSampleOptions.size;

    return {
      selected: result.selected,
      disabled: allItemsDisabled,
      disabledItems: result.disabledItems,
    };
  }, [
    plannedSampleOptions,
    plannedSampleToTargets,
    activeTargetType,
    selectedGroupTargets,
    selectedRegisteredAnimalTargets,
    selectedPlannedAnimalTargets,
    plannedSampleToTargetType,
  ]);

  return (
    <FuzzySearchSelect<PlannedSampleOption, SampleDefinitionFormData['api_id']>
      columns={columns}
      items={plannedSampleOptions}
      disabled={disabled}
      disabledItems={disabledItems}
      searchIndex={searchIndex}
      selected={selected}
      onSelectionChange={(value) => updateSelection({ type: 'planned-sample', targetType: activeTargetType, value })}
      testId="planned-sample-search-select"
    />
  );
};
